import { EmbedType, MeetingSummaryElement } from '../../models'
import { getEditorStateFromHtml, getHtmlContentFromState, getTextContentFromState, isEditorRichText } from '../../../MeetingResults/Lexical/utils'
import { copyResult, getEmptyCopyContent } from '../../../copy'
import { User } from '../../../../../../../models/User'
import { formatDateDDMMYYYY, formatDateHM, getUserDisplayName } from '../../../../common/aifeatures/utils'
import { getPresetResultsByInputParameterHierarchy } from '../../../utils'
import { useAppSelector } from '../../../../../../../store/hooks'
import { selectSelectedMeetingTemplate } from '../../../../../../../store/meetingSummary/meetingTemplates'
import { selectAiFeatures } from '../../../../../../../store/meetingSummary/aiFeatures'
import { selectAiResults } from '../../../../../../../store/meetingSummary/aiResults'
import { selectInputParametersHierarchyForSelectedMeetingTemplate } from '../../../../../../../store/meetingSummary/inputParametersHierarchy'
import { selectRoomSession, selectRoomSessionMeetingTitle } from '../../../../../../../store/meetingSummary/roomSession'
import { useCopyData } from '../../../hooks'
import { useMeetingUsers } from '../../../hooks/users'
import { UserConsentState } from '../../../../../../../components/Consent/model'
import { CONSENT_EXCLUDED_ROLES } from '../../../ConfigureAssignment/AssignmentRoleNameMapping'
import { selectMeetingSummaryAgentElements } from '../../../../../../../store/meetingSummaryAgent/meetingSummaryAgent'
import { CopyContent, HTML_TYPE, TEXT_TYPE } from '../../../../../../../common/copy'

const _CONSENT_TEXT_MAPPING: { [key in UserConsentState]: string } = {
  unknown: '(Consent: Unknown)',
  refused: '(Consent: Refused)',
  revoked: '(Consent: Revoked)',
  accepted: '(Consent: Accepted)',
}

export const useMeetingSummaryElementsCopy = () => {
  const elements = useAppSelector(selectMeetingSummaryAgentElements)
  const selectedMeetingTemplate = useAppSelector(selectSelectedMeetingTemplate)
  const aiFeatures = useAppSelector(selectAiFeatures)
  const aiResults = useAppSelector(selectAiResults)
  const inputParametersHierarchy = useAppSelector(selectInputParametersHierarchyForSelectedMeetingTemplate)
  const roomSession = useAppSelector(selectRoomSession)
  const meetingTitle = useAppSelector(selectRoomSessionMeetingTitle)
  const copyData = useCopyData()
  const { usersByRoles, usersConsent, getSpeakingPercentage } = useMeetingUsers(roomSession)

  const getElementCopyContent = (element: MeetingSummaryElement) => {
    const { type } = element
    switch (type) {
      case 'text':
        const { text } = element
        return getFreeTextCopyContent(text)
      case 'embed':
        const { embedType } = element
        switch (embedType) {
          case EmbedType.PARTICIPANTS:
            return getParticipantsCopyContent()
          case EmbedType.MEETING_RESULT:
            const {
              parameters: { resultTypeId },
            } = element
            return getMeetingResultCopyContent(resultTypeId)
          case EmbedType.MEETING_INFO:
            return getMeetingInfoCopyContent()
          default:
            return getEmptyCopyContent()
        }
      default:
        return getEmptyCopyContent()
    }
  }

  const getFreeTextCopyContent = (text: string) => {
    try {
      const editorState = getEditorStateFromHtml(text)
      const isRichText = isEditorRichText(editorState)

      return {
        [TEXT_TYPE]: isRichText ? getTextContentFromState(JSON.stringify(editorState)) : text,
        [HTML_TYPE]: isRichText ? text : getHtmlContentFromState(JSON.stringify(editorState)),
      }
    } catch (e) {
      console.error(e)
    }
    return { [TEXT_TYPE]: '', [HTML_TYPE]: '' }
  }

  const getConsentTextForUser = (user: User) => {
    if (!usersConsent || !user.roles.every((role) => !CONSENT_EXCLUDED_ROLES.includes(role.id))) {
      return ''
    }

    const consent = usersConsent[user.id]
    if (!consent) {
      return _CONSENT_TEXT_MAPPING[UserConsentState.UNKNOWN]
    }
    return _CONSENT_TEXT_MAPPING[consent.current.state]
  }

  const getParticipantsCopyContent = () => {
    const textContent = Object.entries(usersByRoles)
      .map(([role, users]) => {
        return `${role}\n${users
          .map((u) => {
            const speakingPercentage = getSpeakingPercentage(u)
            return `${getUserDisplayName(u)}(${speakingPercentage}%)${getConsentTextForUser(u)}`
          })
          .join()}`
      })
      .join('\n\n')
    const htmlContent = Object.entries(usersByRoles)
      .map(([role, users]) => {
        return `<p><b>${role}</b></p><p>${users
          .map((u) => {
            const speakingPercentage = getSpeakingPercentage(u)
            return `${getUserDisplayName(u)}(${speakingPercentage}%)${getConsentTextForUser(u)}`
          })
          .join()}</p>`
      })
      .join('')
    return {
      [TEXT_TYPE]: `\n${textContent}\n`,
      [HTML_TYPE]: htmlContent,
    }
  }

  const getMeetingResultCopyContent = (aiFeaturePresetId?: string) => {
    if (!aiFeaturePresetId || !selectedMeetingTemplate || !inputParametersHierarchy) {
      return getEmptyCopyContent()
    }
    const preset = selectedMeetingTemplate.presets.find((p) => p.aiFeaturePresetId === aiFeaturePresetId)
    const aiFeature = preset ? aiFeatures.find((aiFeature) => aiFeature.id === preset.aiFeatureId) : null
    if (!aiFeature || !preset) {
      return getEmptyCopyContent()
    }
    const { inputParametersHierarchyNodeByPresets } = inputParametersHierarchy
    const presetAiResults = getPresetResultsByInputParameterHierarchy(aiFeaturePresetId, aiFeature.id, aiResults, inputParametersHierarchyNodeByPresets).filter(
      (r) => r.status !== 'no_result',
    )
    if (!presetAiResults.length) {
      return getEmptyCopyContent()
    }
    const header = `${aiFeature?.name}`
    const copiedResult = copyResult(aiFeature, presetAiResults[0], copyData)
    return {
      [TEXT_TYPE]: header + '\n\n' + copiedResult[TEXT_TYPE],
      [HTML_TYPE]: `<h3>${header}</h3>` + copiedResult[HTML_TYPE],
    }
  }

  const getMeetingInfoCopyContent = () => {
    if (!roomSession) {
      return getEmptyCopyContent()
    }

    const htmlContent = `
        <p>
        <strong>${meetingTitle}</strong>
        <p>${formatDateDDMMYYYY(roomSession.startedAt)}</p>
        <p>${formatDateHM(roomSession.startedAt)} - ${roomSession.finishedAt ? formatDateHM(roomSession.finishedAt) : ''}</p>
        </p>
    `
    const textContent = `
        ${meetingTitle}
        ${formatDateDDMMYYYY(roomSession.startedAt)}
        ${formatDateHM(roomSession.startedAt)} - ${roomSession.finishedAt ? formatDateHM(roomSession.finishedAt) : ''}
    `
    return {
      [TEXT_TYPE]: textContent,
      [HTML_TYPE]: htmlContent,
    }
  }

  const getCopyContent = (): CopyContent => {
    return elements.reduce<CopyContent>((copyContents, element) => {
      const elementCopyContent = getElementCopyContent(element)
      copyContents[TEXT_TYPE] += elementCopyContent[TEXT_TYPE]
      copyContents[HTML_TYPE] += elementCopyContent[HTML_TYPE]
      return copyContents
    }, getEmptyCopyContent())
  }

  return {
    getCopyContent,
  }
}
